<script setup lang="ts">
// Every trustline on one investor's custody wallet, working or not.
//
// Two axes per row, and they are independent (WAL-03 / WAL-06):
//   • STATUS      — the trustline's own lifecycle (`trustlineStatus`)
//   • ISSUER AUTH — whether the issuer has authorised it (`peerAuthorized`)
// A line can be `active` on the ledger and still unauthorised, which is exactly
// the state that leaves an order parked at `processing_opt_in`. Collapsing the
// two columns would hide it.
//
// ISSUER AUTH is three-state and the grammar is shared with the orders module —
// see @shared/constants/trustlineAuthorization. Nothing in this file decides
// what a null means.
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { truncateMiddle } from '@shared/utils'
import {
  peerAuthorizationColor,
  peerAuthorizationLabel,
} from '@shared/constants/trustlineAuthorization'

import { trustlineStatusColor, trustlineStatusLabel } from '../constants/trustlineStatus'
import type { WalletManagementTrustline } from '../types/cryptoWalletTypes'

interface Props {
  trustlines: WalletManagementTrustline[]
  loading: boolean
}

defineProps<Props>()

// Percentages, not px. The kit table is `table-layout: fixed` with clipped
// overflow: `minWidth` is ignored and px widths that out-sum the container get
// cut off at the right edge (the Copy button lost half its width).
const COLUMNS: TableColumn[] = [
  { key: 'property', label: 'PROPERTY', width: '18%' },
  { key: 'token', label: 'TOKEN', width: '18%' },
  { key: 'issuer', label: 'ISSUER', width: '15%' },
  { key: 'status', label: 'STATUS', width: '13%' },
  { key: 'issuerAuth', label: 'ISSUER AUTH', width: '14%' },
  { key: 'txHash', label: 'TRUSTSET TX', width: '22%' },
]

// A TrustSet hash is 64 hex characters — far past what a cell can show, and
// head-only truncation would strip the tail that tells two hashes apart (XRPL
// hashes routinely share a leading run). The full value stays reachable through
// the copy affordance beside it.
const TX_HASH_HEAD = 8
const TX_HASH_TAIL = 6
</script>

<template>
  <DataTableV2
    :columns="COLUMNS"
    :rows="trustlines"
    :loading="loading"
    :searchable="false"
    row-key="id"
    title="Trustlines"
    empty-title="No trustlines yet"
    empty-subtitle="This wallet has not agreed to hold any property token."
    density="comfortable"
    flat
  >
    <template #cell-property="{ row }">
      <Box flex direction="col" class="cell-stack">
        <Text
          type="body-sm"
          weight="medium"
          truncate
          :title="(row as WalletManagementTrustline).propertyName || undefined"
        >
          {{ (row as WalletManagementTrustline).propertyName || '—' }}
        </Text>
        <Text
          type="caption"
          color="muted"
          truncate
          :title="(row as WalletManagementTrustline).offeringTitle || undefined"
        >
          {{ (row as WalletManagementTrustline).offeringTitle }}
        </Text>
      </Box>
    </template>

    <template #cell-token="{ row }">
      <Box flex direction="col" class="cell-stack">
        <Text type="body-sm" truncate>{{ (row as WalletManagementTrustline).tokenSymbol }}</Text>
        <Text
          type="caption"
          color="muted"
          family="mono"
          truncate
          :title="(row as WalletManagementTrustline).tokenHex || undefined"
        >
          {{ (row as WalletManagementTrustline).tokenHex || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-issuer="{ row }">
      <Text
        type="body-sm"
        family="mono"
        truncate
        :title="(row as WalletManagementTrustline).issuerWalletAddress || undefined"
      >
        {{ (row as WalletManagementTrustline).issuerWalletAddress }}
      </Text>
    </template>

    <template #cell-status="{ row }">
      <Box flex direction="col" gap="1">
        <StatusBadge
          class="cell-badge"
          :color="trustlineStatusColor((row as WalletManagementTrustline).trustlineStatus)"
          :label="trustlineStatusLabel((row as WalletManagementTrustline).trustlineStatus)"
        />
        <!-- The ledger's own reason, kept next to the status it explains. -->
        <Text
          v-if="(row as WalletManagementTrustline).failureReason"
          type="caption"
          color="danger"
          :truncate="2"
        >
          {{ (row as WalletManagementTrustline).failureReason }}
        </Text>
      </Box>
    </template>

    <!-- Three-state. `peerAuthorizationColor` returns `neutral` for a null, so
         an unread ledger never renders as a failure or as a refusal. -->
    <template #cell-issuerAuth="{ row }">
      <StatusBadge
        class="cell-badge"
        :color="peerAuthorizationColor((row as WalletManagementTrustline).peerAuthorized)"
        :label="peerAuthorizationLabel((row as WalletManagementTrustline).peerAuthorized)"
      />
    </template>

    <template #cell-txHash="{ row }">
      <Box
        v-if="(row as WalletManagementTrustline).txHash"
        flex
        align="center"
        gap="1"
        class="tx-cell"
      >
        <Text type="caption" family="mono" class="tx-hash">
          {{
            truncateMiddle((row as WalletManagementTrustline).txHash, TX_HASH_HEAD, TX_HASH_TAIL)
          }}
        </Text>
        <CopyButton :value="(row as WalletManagementTrustline).txHash" label="Copy" />
      </Box>
      <!-- No hash means no TrustSet has been submitted for this line yet — a
           fact about the line, not a value that failed to load. -->
      <Text v-else type="caption" color="muted">Not submitted</Text>
    </template>
  </DataTableV2>
</template>

<style scoped>
/* Flex children default to `min-width: auto`, which blocks `truncate` from
   ellipsising the token hex / names and lets them push past the cell. */
.cell-stack {
  min-width: 0;
}

/* One line on wide screens; on narrow ones Copy drops under the hash rather
   than being clipped by the table's hidden overflow. */
.tx-cell {
  flex-wrap: wrap;
  row-gap: 4px;
  min-width: 0;
}

.tx-hash {
  white-space: nowrap;
}

/* StatusBadge is `nowrap`; let long labels ("Awaiting issuer") wrap inside a
   narrow cell. Doubled class out-ranks the badge's own rule. */
.cell-badge.cell-badge {
  max-width: 100%;
  white-space: normal;
  line-height: 1.3;
}
</style>
